import { BookablePeriod, Service } from '@mews-commander/data-access';
import { BookablePeriodTextKey } from '@mews-commander/types';
import { ServiceTimeClass, TimeUnit } from '@mews/enums';
import { createConditionSwitch } from '@mews/fp';
import { DurationUtils } from '@mews/framework';
import { createHandler } from '@mews/std';
import { TextKey } from '@mews-commander/enums';
import { getData } from './services';

export const getTimeUnitTextKeyByServiceTimeClass = createHandler<ServiceTimeClass, BookablePeriodTextKey>({
    [ServiceTimeClass.Day]: TextKey.Day,
    [ServiceTimeClass.Night]: TextKey.Night,
    [ServiceTimeClass.Hour]: TextKey.Hour,
    [ServiceTimeClass.Month]: TextKey.Month,
});

export const getParentTimeUnit = createHandler({
    [TimeUnit.Hour]: TimeUnit.Day,
    [TimeUnit.Day]: TimeUnit.Month,
    [TimeUnit.Month]: TimeUnit.Month,
});

export const getChildTimeUnit = createHandler({
    [TimeUnit.Hour]: TimeUnit.Hour,
    [TimeUnit.Day]: TimeUnit.Hour,
    [TimeUnit.Month]: TimeUnit.Day,
});

export const getParentTimeUnitPluralTextKey = createHandler({
    [TimeUnit.Hour]: TextKey.Days,
    [TimeUnit.Day]: TextKey.Months,
    [TimeUnit.Month]: TextKey.Months,
});

export const getChildTimeUnitPluralTextKey = createHandler({
    [TimeUnit.Hour]: TextKey.Hours,
    [TimeUnit.Day]: TextKey.Hours,
    [TimeUnit.Month]: TextKey.Days,
});

export const getBookablePeriodPluralTextKey = createHandler<BookablePeriod, Mews.TextKey>({
    [BookablePeriod.Hour]: TextKey.Hours,
    [BookablePeriod.Day]: TextKey.Days,
    [BookablePeriod.Night]: TextKey.NightPlural,
    [BookablePeriod.Month]: TextKey.Months,
});

export const getTimeClass = (service: Service) => {
    const { timeUnit, endOffset } = getData(service);
    return createConditionSwitch([
        {
            cond: timeUnit === TimeUnit.Day && !DurationUtils.isOverlappingNextTimeUnit(endOffset),
            value: ServiceTimeClass.Day,
        },
        { cond: timeUnit === TimeUnit.Day, value: ServiceTimeClass.Night },
        { cond: timeUnit === TimeUnit.Month, value: ServiceTimeClass.Month },
        { cond: timeUnit === TimeUnit.Hour, value: ServiceTimeClass.Hour },
    ]);
};

export const getBookablePeriodTextKey = (bookableService: Service): BookablePeriodTextKey => {
    const timeClass = getTimeClass(bookableService);
    return timeClass ? getTimeUnitTextKeyByServiceTimeClass(timeClass) : TextKey.Day;
};

const bookablePeriodTextKeyToBookablePeriod = createHandler<BookablePeriodTextKey, BookablePeriod>({
    [TextKey.Hour]: BookablePeriod.Hour,
    [TextKey.Day]: BookablePeriod.Day,
    [TextKey.Night]: BookablePeriod.Night,
    [TextKey.Month]: BookablePeriod.Month,
});

export const getBookablePeriod = (bookableService: Service): BookablePeriod => {
    const textKey = getBookablePeriodTextKey(bookableService);
    return bookablePeriodTextKeyToBookablePeriod(textKey);
};

export const isDayUse = (service: Service) => {
    const { timeUnit, endOffset } = getData(service);
    return timeUnit === TimeUnit.Day && !DurationUtils.isOverlappingNextTimeUnit(endOffset);
};
export const isNightUse = (service: Service) => {
    const { timeUnit, endOffset } = getData(service);
    return timeUnit === TimeUnit.Day && DurationUtils.isOverlappingNextTimeUnit(endOffset);
};
export const isMonthUse = (service: Service) => {
    const { timeUnit } = getData(service);
    return timeUnit === TimeUnit.Month;
};
export const isHourUse = (service: Service) => {
    const { timeUnit } = getData(service);
    return timeUnit === TimeUnit.Hour;
};
